import { useCallback, useEffect, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import { getAdminDemos, updateAdminDemo } from "../apicalls/adminDemos";
import "./style/AdminDemos.css";

const STATUS_OPTIONS = ["pending_payment", "open", "partially_returned", "returned", "cancelled"];
const PAYMENT_STATUS_OPTIONS = ["pending", "paid", "failed", "waived"];
const ACTIVE_DEMO_STATUSES = "pending_payment,open,partially_returned";
const ARCHIVE_DEMO_STATUSES = "returned,cancelled";

function statusLabel(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AdminDemos() {
  const token = isAuthenticated()?.access_token;
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: ACTIVE_DEMO_STATUSES });
  const [editDemo, setEditDemo] = useState(null);

  const fetchDemos = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminDemos(token, filters);
      if (data && Array.isArray(data.demos)) {
        setDemos(data.demos);
      } else {
        setError("Failed to load demos");
      }
    } catch (err) {
      setError(err.message || "Failed to load demos");
    }
    setLoading(false);
  }, [filters, token]);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === status ? undefined : status,
    }));
  };

  const handlePaymentFilter = (ps) => {
    setFilters((prev) => ({
      ...prev,
      payment_status: prev.payment_status === ps ? undefined : ps,
    }));
  };

  const handleViewFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status,
    }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: value || undefined,
    }));
  };

  const handleEdit = async (demoId, field, value) => {
    try {
      await updateAdminDemo(demoId, { [field]: value }, token);
      setEditDemo(null);
      fetchDemos();
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const openEdit = (demo) => {
    setEditDemo({ ...demo });
  };

  if (!token) return null;

  return (
    <main>
    <div className="admin-demos">
      <h1>Demo Racket Loans</h1>

      <div className="admin-demos-filters">
        <div className="filter-group">
          <span>View:</span>
          <button
            className={`filter-btn ${filters.status === ACTIVE_DEMO_STATUSES ? "active" : ""}`}
            onClick={() => handleViewFilter(ACTIVE_DEMO_STATUSES)}
          >
            Active
          </button>
          <button
            className={`filter-btn ${filters.status === ARCHIVE_DEMO_STATUSES ? "active" : ""}`}
            onClick={() => handleViewFilter(ARCHIVE_DEMO_STATUSES)}
          >
            Archive
          </button>
        </div>
        <div className="filter-group">
          <span>Status:</span>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`filter-btn ${filters.status === s ? "active" : ""}`}
              onClick={() => handleStatusFilter(s)}
            >
              {statusLabel(s)}
            </button>
          ))}
          <button
            className={`filter-btn ${filters.status === undefined ? "active" : ""}`}
            onClick={() => setFilters((p) => ({ ...p, status: undefined }))}
          >
            All
          </button>
        </div>
        <div className="filter-group">
          <span>Payment:</span>
          {PAYMENT_STATUS_OPTIONS.map((ps) => (
            <button
              key={ps}
              className={`filter-btn ${filters.payment_status === ps ? "active" : ""}`}
              onClick={() => handlePaymentFilter(ps)}
            >
              {statusLabel(ps)}
            </button>
          ))}
        </div>
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search client name, email, phone..."
            value={filters.search || ""}
            onChange={handleSearch}
          />
        </div>
      </div>

      {loading && <div className="spinner" />}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <table className="admin-demos-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vendor</th>
              <th>Client</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Checkout</th>
              <th>Due</th>
              <th>Lines</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {demos.length === 0 && (
              <tr><td colSpan="9">No demos found</td></tr>
            )}
            {demos.map((d) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td>{d.vendor_name}</td>
                <td>{d.client_name}</td>
                <td>
                  <span className={`demo-badge demo-badge-${d.status}`}>
                    {statusLabel(d.status)}
                  </span>
                </td>
                <td>
                  <span className={`demo-pay-badge demo-pay-${d.payment_status}`}>
                    {statusLabel(d.payment_status)}
                  </span>
                </td>
                <td>{d.checkout_on ? d.checkout_on.slice(0, 10) : "-"}</td>
                <td>{d.due_on ? d.due_on.slice(0, 10) : "-"}</td>
                <td>{d.line_count || 0} / {d.returned_count || 0}</td>
                <td>
                  <button className="btn-small" onClick={() => openEdit(d)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editDemo && (
        <div className="modal-overlay" onClick={() => setEditDemo(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Demo #{editDemo.id}</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const status = fd.get("status");
                const payment_status = fd.get("payment_status");
                handleEdit(editDemo.id, "status", status);
                if (payment_status) handleEdit(editDemo.id, "payment_status", payment_status);
              }}
            >
              <label>
                Status:
                <select name="status" defaultValue={editDemo.status}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              </label>
              <label>
                Payment Status:
                <select name="payment_status" defaultValue={editDemo.payment_status}>
                  {PAYMENT_STATUS_OPTIONS.map((ps) => (
                    <option key={ps} value={ps}>{statusLabel(ps)}</option>
                  ))}
                </select>
              </label>
              <div className="modal-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => setEditDemo(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </main>
  );
}
